import React from "react"
import {Zap, Shield, Wrench, CheckCircle} from 'lucide-react'

const HondaPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-500 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Honda Generator Auto Start Solutions
            </h1>
            <p className="text-xl text-red-50 leading-relaxed mb-4">
              Premium remote start generator kits and autostart modules designed specifically for Honda EU, EB, and EG series generators.
            </p>
            <p className="text-lg text-red-100">
              Known for reliability and quiet operation, Honda generators are ideal candidates for automation upgrades.
            </p>
          </div>
        </div>
      </section>

      {/* Compatible Models */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Compatible Honda Generator Models
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">EU Series (Inverter)</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EU2200i / EU2000i
                </li>
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EU3000is / EU3000i Handi
                </li>
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EU7000is / EU6500is
                </li>
                <li className="text-sm text-gray-600 mt-3">
                  *Requires electric start option
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">EB Series (Industrial)</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EB2800i / EB3000c
                </li>
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EB5000i / EB6500
                </li>
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EB10000 / EB11000
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">EG Series (Economy)</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EG2800i / EG4000
                </li>
                <li className="flex items-center">
                  <CheckCircle size={20} className="text-red-500 mr-2" />
                  EG5000 / EG6500CL
                </li>
                <li className="text-sm text-gray-600 mt-3">
                  *Most models have electric start
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Why Automate Your Honda Generator?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <Zap size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Automatic Operation</h3>
              <p className="text-gray-600">
                Start your generator remotely or automatically when power is needed
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <Shield size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Solar Integration</h3>
              <p className="text-gray-600">
                Seamlessly integrate with solar systems for battery backup charging
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <Wrench size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">RV Convenience</h3>
              <p className="text-gray-600">
                Control from inside your RV without going outside
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <CheckCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Preserve Warranty</h3>
              <p className="text-gray-600">
                Non-invasive installation maintains Honda factory warranty
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Types */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Installation Options for Honda Generators
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Remote Start Kit
              </h3>
              <p className="text-gray-700 mb-4">
                Add wireless remote control capability to any Honda generator with electric start. Includes key fob transmitter and receiver module.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Up to 300ft range (line of sight)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>2-wire connection to starter circuit</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Multiple key fobs supported</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Installation time: 1-2 hours</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Auto Start Module (2-Wire)
              </h3>
              <p className="text-gray-700 mb-4">
                Automatic generator starting based on external signals from solar inverters, transfer switches, or voltage sensors.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Victron/Growatt solar integration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Battery voltage monitoring triggers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Automatic shutdown when charged</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✓</span>
                  <span>Installation time: 2-4 hours</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Honda Generator Technical Specifications
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-md">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="py-3 px-4 font-semibold text-gray-900">Parameter</th>
                    <th className="py-3 px-4 font-semibold text-gray-900">Specification</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4">Starter System Voltage</td>
                    <td className="py-3 px-4">12V DC</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4">Starter Current Draw</td>
                    <td className="py-3 px-4">80-150A (varies by model)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4">Control Signal Type</td>
                    <td className="py-3 px-4">Dry contact closure (2-wire)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4">Recommended Wire Gauge</td>
                    <td className="py-3 px-4">10-12 AWG for control signals</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4">Relay Rating Required</td>
                    <td className="py-3 px-4">30A minimum, 12V coil</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Choke Control</td>
                    <td className="py-3 px-4">Manual (most models) / Auto (select inverters)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 p-4 bg-orange-50 border-l-4 border-orange-500 rounded">
            <p className="text-gray-700">
              <strong>Important:</strong> Honda EU series inverter generators with fuel injection typically don't require choke adjustment. Older carbureted models may need manual choke positioning before remote start operation.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Automate Your Honda Generator?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Explore our comprehensive installation guides and product recommendations for Honda generator automation.
          </p>
        </div>
      </section>
    </div>
  )
}

export default HondaPage